/**
 * Hierarchical Location Service
 * Direct Firestore access for Region → Country → Location hierarchy
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  increment,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { apiRequest } from './api';
import type { Region, Country, Location } from '@simcheck/shared-types';

// Helper to remove undefined values
function removeUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  const result: any = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  });
  return result;
}

// Helper to convert Firestore Timestamp or ISO string to Date
function toDate(value: any): Date | undefined {
  if (!value) return undefined;
  if (value.toDate && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value === 'string') {
    return new Date(value);
  }
  if (value instanceof Date) {
    return value;
  }
  return undefined;
}

/**
 * Get all regions
 */
export async function getRegions(): Promise<Region[]> {
  const regionsRef = collection(db, 'regions');
  const q = query(regionsRef, orderBy('name', 'asc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: toDate(doc.data().createdAt),
    updatedAt: toDate(doc.data().updatedAt),
  } as Region));
}

/**
 * Get all countries (optionally filtered by region)
 */
export async function getCountries(regionId?: string): Promise<Country[]> {
  const countriesRef = collection(db, 'countries');
  let q = query(countriesRef, orderBy('name', 'asc'));
  
  if (regionId) {
    q = query(countriesRef, where('regionId', '==', regionId), orderBy('name', 'asc'));
  }
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: toDate(doc.data().createdAt),
    updatedAt: toDate(doc.data().updatedAt),
  } as Country));
}

/**
 * Get all locations (optionally filtered by country)
 */
export async function getLocations(countryId?: string): Promise<Location[]> {
  const locationsRef = collection(db, 'locations');
  let q = query(locationsRef, orderBy('name', 'asc'));

  if (countryId) {
    q = query(locationsRef, where('countryId', '==', countryId), orderBy('name', 'asc'));
  }

  const snapshot = await getDocs(q);

  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: toDate(doc.data().createdAt),
      updatedAt: toDate(doc.data().updatedAt),
    } as Location));
}

/**
 * Get full hierarchical location data
 * Returns regions, countries, and locations (optionally filtered by organization)
 * Calculates counts dynamically based on actual child relationships
 */
export async function getFullHierarchicalLocations(): Promise<{ regions: Region[]; countries: Country[]; locations: Location[] }> {
  const [regions, countries, locations] = await Promise.all([
    getRegions(),
    getCountries(),
    getLocations(),
  ]);

  return { regions, countries, locations };
}

/**
 * Tenancy filter (#202): tenants see shared locations (ownerOrgId null) plus
 * their own org's private locations — NEVER another org's. The `locations`
 * collection is client-readable in full (list-query rules trap prevents
 * per-doc read rules), so every UI surface that lists locations MUST run its
 * result through this before rendering. Super admins see everything.
 *
 * Exception — synthetic demo locations (`syntheticDemo: true`, e.g. the
 * AutoConnect Global "… Lab" fleet) are seeded furniture with no hardware
 * behind them. They are hard-scoped to their owning org **above** the
 * super-admin bypass, mirroring `isSyntheticDemoVisible()` for devices: a
 * super-admin viewing any other tenant must not see them, and they reappear
 * as soon as that super-admin switches into the owning org. Without this the
 * devices correctly disappear but their locations don't, so every other
 * tenant's picker fills up with "0/0 modems" ghost sites.
 */
export function filterLocationsByTenancy<
  T extends { id: string; ownerOrgId?: string | null; syntheticDemo?: boolean; isDefault?: boolean }
>(
  locations: T[],
  activeOrgId: string | null | undefined,
  isSuperAdmin: boolean,
  opts?: {
    /**
     * Whether a device is actually parked at a location. When supplied, an org
     * default location with no device is hidden — an empty auto-created default
     * is scaffolding, not somewhere you can run a test. Surfaces that don't
     * load devices simply omit this and keep the org-scoping rules below.
     */
    hasDeviceAt?: (locationId: string) => boolean;
  },
): T[] {
  return locations.filter(l => {
    const ownedByActiveOrg = !!activeOrgId && l.ownerOrgId === activeOrgId;
    // Synthetic demo locations: seeded furniture, own org only (see above).
    if (l.syntheticDemo) return ownedByActiveOrg;
    // Org default locations (`location-org-default-<orgId>`) are per-tenant
    // scaffolding auto-created at bootstrap. Hard-scope them to their owning
    // org ABOVE the super-admin bypass — browsing as one tenant should surface
    // that tenant's default, never all twelve orgs' empty defaults — and drop
    // them entirely when nothing is deployed there.
    if (l.isDefault) return ownedByActiveOrg && (opts?.hasDeviceAt?.(l.id) ?? true);
    if (isSuperAdmin) return true;
    return !l.ownerOrgId || l.ownerOrgId === activeOrgId;
  });
}

/**
 * Filter hierarchy to only include countries/regions that contain the given locations.
 * Call this AFTER filtering locations by device tier visibility.
 */
export function filterHierarchy(
  regions: Region[],
  countries: Country[],
  locations: Location[]
): { regions: Region[]; countries: Country[]; locations: Location[] } {
  // Count locations per country
  const countryLocationCounts = new Map<string, number>();
  for (const loc of locations) {
    if (loc.countryId) {
      countryLocationCounts.set(loc.countryId, (countryLocationCounts.get(loc.countryId) || 0) + 1);
    }
  }

  // Keep only countries that have locations
  const filteredCountries = countries
    .map(c => ({ ...c, locationCount: countryLocationCounts.get(c.id) || 0 }))
    .filter(c => c.locationCount > 0);

  // Count countries per region
  const regionCountryCounts = new Map<string, number>();
  const regionLocationCounts = new Map<string, number>();
  for (const c of filteredCountries) {
    if (c.regionId) {
      regionCountryCounts.set(c.regionId, (regionCountryCounts.get(c.regionId) || 0) + 1);
      regionLocationCounts.set(c.regionId, (regionLocationCounts.get(c.regionId) || 0) + c.locationCount);
    }
  }

  // Keep only regions that have countries with locations
  const filteredRegions = regions
    .map(r => ({
      ...r,
      countryCount: regionCountryCounts.get(r.id) || 0,
      locationCount: regionLocationCounts.get(r.id) || 0,
    }))
    .filter(r => r.locationCount > 0);

  return { regions: filteredRegions, countries: filteredCountries, locations };
}

/**
 * Create a new region
 */
export async function createRegion(regionData: Omit<Region, 'id' | 'createdAt' | 'updatedAt' | 'countryCount' | 'locationCount' | 'deviceCount' | 'modemCount'>, userId: string): Promise<Region> {
  if (!regionData.name || !regionData.code) {
    throw new Error('Region name and code are required');
  }

  const newRegion: Omit<Region, 'id'> = {
    name: regionData.name,
    code: regionData.code,
    description: regionData.description,
    createdAt: new Date(),
    updatedAt: new Date(),
    createdBy: userId,
    countryCount: 0,
    locationCount: 0,
    deviceCount: 0,
    modemCount: 0,
  };
  
  const docRef = await addDoc(collection(db, 'regions'), newRegion);
  return { id: docRef.id, ...newRegion } as Region;
}

/**
 * Update a region
 */
export async function updateRegion(regionId: string, updates: Partial<Omit<Region, 'id' | 'createdAt' | 'createdBy'>>): Promise<void> {
  const regionRef = doc(db, 'regions', regionId);
  await updateDoc(regionRef, {
    ...removeUndefined(updates),
    updatedAt: new Date(),
  });
}

/**
 * Delete a region
 * Note: Region must have no countries before deletion
 */
export async function deleteRegion(regionId: string): Promise<void> {
  // Check if region has any countries
  const countriesRef = collection(db, 'countries');
  const q = query(countriesRef, where('regionId', '==', regionId));
  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    throw new Error(`Cannot delete region: ${snapshot.size} countries still reference this region. Delete or reassign the countries first.`);
  }

  const regionRef = doc(db, 'regions', regionId);
  await deleteDoc(regionRef);
}

/**
 * Create a new country
 */
export async function createCountry(countryData: Omit<Country, 'id' | 'createdAt' | 'updatedAt' | 'locationCount' | 'deviceCount' | 'modemCount'>, userId: string): Promise<Country> {
  if (!countryData.name || !countryData.code || !countryData.regionId || !countryData.timezone) {
    throw new Error('Country name, code, regionId, and timezone are required');
  }

  const newCountry: Omit<Country, 'id'> = {
    name: countryData.name,
    code: countryData.code,
    regionId: countryData.regionId,
    timezone: countryData.timezone,
    flag: countryData.flag,
    timezones: countryData.timezones,
    createdAt: new Date(),
    updatedAt: new Date(),
    locationCount: 0,
    deviceCount: 0,
    modemCount: 0,
  };
  
  const docRef = await addDoc(collection(db, 'countries'), newCountry);

  // Update parent region
  const regionRef = doc(db, 'regions', countryData.regionId);
  await updateDoc(regionRef, {
    countryIds: arrayUnion(docRef.id),
    countryCount: increment(1),
  });

  return { id: docRef.id, ...newCountry } as Country;
}

/**
 * Update a country
 */
export async function updateCountry(countryId: string, updates: Partial<Omit<Country, 'id' | 'createdAt'>>): Promise<void> {
  const countryRef = doc(db, 'countries', countryId);
  const countrySnap = await getDoc(countryRef);

  if (!countrySnap.exists()) {
    throw new Error('Country not found');
  }

  const currentCountry = countrySnap.data() as Country;

  // If regionId is changing, update both old and new region's country counts
  if (updates.regionId && updates.regionId !== currentCountry.regionId) {
    // Remove from old region
    const oldRegionRef = doc(db, 'regions', currentCountry.regionId);
    await updateDoc(oldRegionRef, {
      countryIds: arrayRemove(countryId),
      countryCount: increment(-1),
    });

    // Add to new region
    const newRegionRef = doc(db, 'regions', updates.regionId);
    await updateDoc(newRegionRef, {
      countryIds: arrayUnion(countryId),
      countryCount: increment(1),
    });
  }

  await updateDoc(countryRef, {
    ...removeUndefined(updates),
    updatedAt: new Date(),
  });
}

/**
 * Delete a country
 * Note: Country must have no locations before deletion
 */
export async function deleteCountry(countryId: string): Promise<void> {
  const countryRef = doc(db, 'countries', countryId);
  const countrySnap = await getDoc(countryRef);

  if (!countrySnap.exists()) {
    throw new Error('Country not found');
  }

  const countryData = countrySnap.data() as Country;

  // Check if country has any locations
  const locationsRef = collection(db, 'locations');
  const q = query(locationsRef, where('countryId', '==', countryId));
  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    throw new Error(`Cannot delete country: ${snapshot.size} locations still reference this country. Delete or reassign the locations first.`);
  }

  // Update parent region to remove this country
  if (countryData.regionId) {
    try {
      const regionRef = doc(db, 'regions', countryData.regionId);
      await updateDoc(regionRef, {
        countryIds: arrayRemove(countryId),
        countryCount: increment(-1),
      });
    } catch (error) {
      console.warn('Failed to update parent region, continuing with deletion:', error);
    }
  }

  // Delete the country
  await deleteDoc(countryRef);
}

/**
 * Create a new location
 */
export async function createLocation(locationData: Omit<Location, 'id' | 'createdAt' | 'updatedAt' | 'deviceCount' | 'onlineDeviceCount' | 'modemCount' | 'availableModemCount'>, userId: string): Promise<Location> {
  if (!locationData.name || !locationData.countryId || !locationData.regionId || !locationData.city || !locationData.timezone) {
    // User-facing message — use the form's labels, not internal field names.
    throw new Error('Name, region, country, city, and timezone are required');
  }

  // Server-mediated for the same reason as updateLocation: owner-only fields
  // must land in the private subcollection, never on the location document.
  // The API also owns tenancy (it forces ownerOrgId for non-super callers) and
  // the parent-country bookkeeping.
  const { location } = await apiRequest<{ location: Location }>('/hierarchy/locations', {
    method: 'POST',
    body: JSON.stringify(
      removeUndefined({
        name: locationData.name,
        countryId: locationData.countryId,
        city: locationData.city,
        timezone: locationData.timezone,
        type: locationData.type,
        status: locationData.status,
        displayName: locationData.displayName,
        description: locationData.description,
        state: locationData.state,
        address: locationData.address,
        postalCode: locationData.postalCode,
        coordinates: locationData.coordinates,
        capacity: locationData.capacity,
        contact: locationData.contact,
        ownerOrgId: (locationData as Partial<Location>).ownerOrgId,
      }),
    ),
  });

  return location;
}

/**
 * Update a location
 */
/**
 * Update a location.
 *
 * Goes through the API rather than writing Firestore directly: owner-only
 * fields (address, contact, notes, description, exact coordinates) live in a
 * server-only subcollection, and only the API can route a field to the right
 * home. A direct client write would put them back on the world-readable
 * location document. The API also re-checks ownership server-side.
 */
export async function updateLocation(locationId: string, updates: Partial<Location>): Promise<void> {
  await apiRequest(`/hierarchy/locations/${locationId}`, {
    method: 'PUT',
    body: JSON.stringify(removeUndefined(updates)),
  });
}

/**
 * Fetch a location including its owner-only detail.
 *
 * The list/tree reads come straight from Firestore, which by design never
 * carries those fields — so the edit form has to ask the API, which merges them
 * back in for callers who own the site (or are super_admin/support).
 */
export async function getLocationWithPrivateDetails(locationId: string): Promise<Location> {
  const { location } = await apiRequest<{ location: Location }>(`/hierarchy/locations/${locationId}`);
  return location;
}

/**
 * Delete a location
 */
export async function deleteLocation(locationId: string): Promise<void> {
  const locationRef = doc(db, 'locations', locationId);
  const locationSnap = await getDoc(locationRef);
  
  if (!locationSnap.exists()) {
    throw new Error('Location not found');
  }

  const locationData = locationSnap.data() as Location;

  // Update parent country to remove this location (if countryId exists)
  if (locationData.countryId) {
    try {
      const countryRef = doc(db, 'countries', locationData.countryId);
      await updateDoc(countryRef, {
        locationIds: arrayRemove(locationId),
        locationCount: increment(-1),
      });
    } catch (error) {
      console.warn('Failed to update parent country, continuing with deletion:', error);
      // Continue with deletion even if country update fails
    }
  }

  // Delete the location
  await deleteDoc(locationRef);
}
